import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { UnitFormula, UnitFormulaCreateRequest } from '../../types/admin';

interface UnitFormulaEditDialogProps {
  formula: UnitFormula | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UnitFormulaCreateRequest) => Promise<void>;
  isSaving: boolean;
}

export default function UnitFormulaEditDialog({
  formula,
  isOpen,
  onClose,
  onSave,
  isSaving,
}: UnitFormulaEditDialogProps) {
  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<UnitFormulaCreateRequest>({
    defaultValues: formula ? {
      unitId: formula.unitId,
      nameLong: formula.nameLong,
      nameShort: formula.nameShort,
      baseToDisp: formula.baseToDisp,
      dispToBase: formula.dispToBase,
      description: formula.description,
    } : undefined,
  });

  // Reset form when formula changes
  useEffect(() => {
    if (formula) {
      reset({
        unitId: formula.unitId,
        nameLong: formula.nameLong,
        nameShort: formula.nameShort,
        baseToDisp: formula.baseToDisp,
        dispToBase: formula.dispToBase,
        description: formula.description,
      });
    }
  }, [formula, reset]);

  const onSubmit = async (data: UnitFormulaCreateRequest) => {
    await onSave(data);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-2xl font-semibold leading-6 text-gray-900 mb-6">
                    Edit Unit Formula
                  </h3>

                  <div className="space-y-4">
                    {formula && (
                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <span className="text-gray-600">Formula ID:</span>
                        <span className="ml-2 font-medium">{formula.untfrmId}</span>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium mb-2">Unit ID *</label>
                      <input
                        type="number"
                        {...register('unitId', {
                          required: 'Unit ID is required',
                          valueAsNumber: true,
                          min: { value: 1, message: 'Unit ID must be greater than 0' },
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {errors.unitId && (
                        <p className="text-red-600 text-sm mt-1">{errors.unitId.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Long Name *</label>
                      <input
                        type="text"
                        {...register('nameLong', { required: 'Long name is required' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {errors.nameLong && (
                        <p className="text-red-600 text-sm mt-1">{errors.nameLong.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Short Name *</label>
                      <input
                        type="text"
                        {...register('nameShort', { required: 'Short name is required' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {errors.nameShort && (
                        <p className="text-red-600 text-sm mt-1">{errors.nameShort.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Base to Display Formula *</label>
                      <input
                        type="text"
                        {...register('baseToDisp', { required: 'Base to display formula is required' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                        placeholder="e.g., x"
                      />
                      {errors.baseToDisp && (
                        <p className="text-red-600 text-sm mt-1">{errors.baseToDisp.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Display to Base Formula *</label>
                      <input
                        type="text"
                        {...register('dispToBase', { required: 'Display to base formula is required' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                        placeholder="e.g., x"
                      />
                      {errors.dispToBase && (
                        <p className="text-red-600 text-sm mt-1">{errors.dispToBase.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Notes</label>
                      <textarea
                        {...register('description')}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:justify-end gap-3">
              <button
                type="submit"
                disabled={!isDirty || isSaving}
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
